import { Injectable } from '@angular/core';
import CameraControls from 'camera-controls';
import { KeyboardKeyHold } from 'hold-event';
import { Subscription } from 'rxjs';
import { CONSTANTS } from 'src/app/core/constants/constants';
import { GlobalBuilder } from 'src/app/core/models/3D/object-builders/global-builder.model';
import { CameraInstructions } from 'src/app/core/models/3D/utils/camera-instructions';
import { Box3, Vector3 } from 'three';
import { CameraTypes } from '../camera/camera-types';
import { StatesService } from '../states/states.service';

@Injectable({
  providedIn: 'root',
})
export class ControlsService {
  private controls: CameraControls;
  private readonly HIGHER_SPEED = 4;
  private readonly SLOWER_SPEED = 0.5;
  private readonly LARGER_WORD_SIZE = 5000;
  private frontalAxisSpeed = 0.5;
  private lateralAxisSpeed = 0.25;
  private verticalAxisSpeed = 0.25;
  private holdingEv = CONSTANTS.KEY_HOLD_EVENT;
  private subscriptions = new Subscription();

  constructor(private statesService: StatesService) {}

  get target(): Vector3 {
    return this.controls?.getTarget(null) || new Vector3(0, 0, 0);
  }

  private get worldSize(): number {
    return this.statesService.worldSize.getValue();
  }

  set(): void {
    this.subscriptions = new Subscription();
    this.controls = new CameraControls(
      this.statesService.camera.getValue(),
      this.statesService.renderer.getValue().domElement,
    );

    this.statesService.controls.next(this.controls);
    this.setControlsConfig();
    this.watchDisposeEvent();
    this.watchCameraChanges();
    this.watchWorldSizeChanges();
    this.watchFocusEvent();
  }

  zoomIn(): void {
    const cameraType = this.statesService.cameraType.getValue();
    this.controls.camera;
    if (cameraType === CameraTypes.PERSPECTIVE) {
      this.controls.dolly(this.controls.distance * 0.5, true);
    } else {
      this.controls.zoom(this.controls.camera.zoom * 0.5, true);
    }
  }

  zoomOut(): void {
    const cameraType = this.statesService.cameraType.getValue();
    if (cameraType === CameraTypes.PERSPECTIVE) {
      this.controls.dolly(-this.controls.distance * 0.5, true);
    } else {
      this.controls.zoom(-this.controls.camera.zoom * 0.5, true);
    }
  }

  private setControlsConfig(): void {
    const KEYCODE = { W: 87, A: 65, S: 83, D: 68, E: 69, Q: 81 };

    const keyOptions = [
      {
        code: KEYCODE.W,
        method: (e) => this.controls.forward(this.frontalAxisSpeed * e.deltaTime, false),
      },
      {
        code: KEYCODE.S,
        method: (e) => this.controls.forward(-this.frontalAxisSpeed * e.deltaTime, false),
      },

      {
        code: KEYCODE.D,
        method: (e) => this.controls.truck(this.lateralAxisSpeed * e.deltaTime, 0, false),
      },
      {
        code: KEYCODE.A,
        method: (e) => this.controls.truck(-this.lateralAxisSpeed * e.deltaTime, 0, false),
      },

      {
        code: KEYCODE.E,
        method: (e) => {
          this.controls.setTarget(
            this.target.x,
            this.target.y,
            this.target.z + e.deltaTime * this.verticalAxisSpeed,
            false,
          );
        },
      },
      {
        code: KEYCODE.Q,
        method: (e) => {
          this.controls.setTarget(
            this.target.x,
            this.target.y,
            this.target.z - e.deltaTime * this.verticalAxisSpeed,
            false,
          );
        },
      },
    ];

    const holdIntervalDelay = 16.666;
    keyOptions.forEach((key) => {
      const holdKeyEv = new KeyboardKeyHold(key.code, holdIntervalDelay);
      holdKeyEv.addEventListener(this.holdingEv, key.method);
    });
  }

  private watchDisposeEvent(): void {
    const disposeSubs = this.statesService.dispose.subscribe((_) => {
      this.dispose();
    });
    this.subscriptions.add(disposeSubs);
  }

  private watchCameraChanges(): void {
    const cameraSubs = this.statesService.camera.subscribe((camera) => {
      if (camera) {
        this.controls.camera = camera;
        const cameraType = this.statesService.cameraType.getValue();

        if (cameraType === CameraTypes.PERSPECTIVE) {
          this.controls.mouseButtons.wheel = CameraControls.ACTION.DOLLY;
        } else {
          this.controls.mouseButtons.wheel = CameraControls.ACTION.ZOOM;
          this.controls.minZoom = 0.4;
          this.controls.maxZoom = 100;
        }
      }
    });
    this.subscriptions.add(cameraSubs);
  }

  private watchWorldSizeChanges(): void {
    const worldSizeSubs = this.statesService.worldSize.subscribe((_) => {
      this.updateConstrolsByWorldSize();
    });
    this.subscriptions.add(worldSizeSubs);
  }

  private updateConstrolsByWorldSize(): void {
    if (!this.controls) {
      return;
    }
    this.controls.maxDistance = this.worldSize * 5;

    const boxSize = this.worldSize * 2;
    const boxVect = new Vector3(boxSize, boxSize, boxSize);
    const box = new Box3(
      new Vector3().copy(boxVect).multiplyScalar(-1),
      new Vector3().copy(boxVect),
    );
    this.controls.setBoundary(box);

    const newSpeed = this.calculateCameraKeyboardSpeed();
    this.frontalAxisSpeed = newSpeed;
    this.lateralAxisSpeed = newSpeed / 2;
    this.verticalAxisSpeed = newSpeed / 2;
  }

  private calculateCameraKeyboardSpeed(): number {
    const a =
      (this.HIGHER_SPEED - this.SLOWER_SPEED) /
      (this.LARGER_WORD_SIZE - GlobalBuilder.MIN_WORLD_SIZE);
    const b = this.HIGHER_SPEED - a * this.LARGER_WORD_SIZE;

    return a * this.worldSize + b;
  }

  private watchFocusEvent(): void {
    const focusSubs = this.statesService.focus.subscribe((coordinates: CameraInstructions) => {
      this.focusElement(coordinates.target, coordinates.position);
    });
    this.subscriptions.add(focusSubs);
  }

  private focusElement(target: Vector3, camera: Vector3): void {
    this.controls.setLookAt(camera.x, camera.y, camera.z, target.x, target.y, target.z, true);
  }

  private dispose(): void {
    this.controls.dispose();
    this.subscriptions.unsubscribe();
    this.statesService.controls.next(null);
  }
}
